import dayjs from 'dayjs'
import { calculateNotificationDates, getMaxDueDate } from './dates'
import { progressBar } from './progressbar'
import { taskToExternal } from './tasks'

export async function templateToExternal(templateRef, templateData, clientData, clientRef, orgData, orgRef, preview, firebase) {
  try {
    const datePointer = dayjs()
    const batch = firebase.firestore().batch()
    const [Metadata, sections] = await compileTemplate(templateData, orgRef, preview, datePointer)
    Metadata.Sections = {}
    if (sections.length < 1) {
      throw new Error('No Sections with Tasks Found')
    }
    sections.forEach((section, sectionIndex) => {
      console.log(section)
      Metadata.Sections[sectionIndex] = {
        Name: section.Name,
        Note: section.Note || '',
        HideOverviewPage: section.HideOverviewPage || false,
        Data: {}
      }
      let sectionDueDate = dayjs('1990-1-1')
      section.Data.forEach((taskData, taskIndex) => {
        const task = taskData.Task
        const dueDate = datePointer.add(taskData.EndDateOffset, 'days')
        task.DueDate = firebase.firestore.Timestamp.fromDate(dueDate.toDate())
        if (sectionDueDate.isBefore(dueDate)) sectionDueDate = dueDate
        const cadence = Metadata.NotificationCadence
        task.NotificationDates = calculateNotificationDates({
          startDate: datePointer,
          startDateOffset: taskData.StartDateOffset,
          endDateOffset: taskData.EndDateOffset,
          maxReminders: cadence.MaxReminders,
          reminderCadence: cadence.ReminderCadence,
          setReminderCadence: cadence.SetReminderCadence,
          reminderDaysBeforeDue: cadence.ReminderDaysBeforeDue,
          setReminderDaysBeforeDue: cadence.SetReminderDaysBeforeDue
        })
        const newTaskRef = clientRef.collection('Templates').doc()
        Metadata.Sections[sectionIndex]['Data'][taskIndex] = {
          Name: task.Name,
          EstimatedDuration: task.EstimatedDuration || 432000, //5 days
          StartDateOffset: taskData.StartDateOffset,
          EndDateOffset: taskData.EndDateOffset,
          TaskType: task.TaskType,
          Note: task.Note || {},
          ClientResponsibilities: task.ClientResponsibilities || [],
          InternalResponsibilities: task.InternalResponsibilities || [],
          RequireInternalConfirmation: task.RequireInternalConfirmation || false,
          Ref: newTaskRef.id,
          Dependency: taskData.Dependency || {},
          TaskID: taskData.TaskID,
          DueDate: task.DueDate,
          NotificationDates: task.NotificationDates,
          TaskEmail: task.TaskEmail || {},
          NotificationCustomizations: taskData.NotificationCustomizations || null
        }
        task.TaskID = taskData.TaskID
        batch.set(newTaskRef, task)
      })
      Metadata.Sections[sectionIndex].DueDate = firebase.firestore.Timestamp.fromDate(sectionDueDate.toDate())
    })
    Metadata.Variables = clientData.Metadata.Variables || {} // restore variables from client

    const [, nextTask, , nextSectionIndex, , isTotallyComplete] = progressBar({ sections: Object.values(Metadata.Sections), all: true, skip: false })

    const NextStep = {
      Name: nextTask?.Name || null,
      Type: nextTask?.TaskType || null,
      DueDate: nextTask?.DueDate || null,
      Responsible: 'external',
      TotallyComplete: isTotallyComplete,
      SectionIndex: nextSectionIndex,
      TaskRef: nextTask?.Ref || null,
      TaskID: nextTask?.TaskID || null
    }
    if (nextTask?.TaskType === 'Internal Process') NextStep.Responsible = 'internal'
    if (nextTask?.TaskType === 'Confirmation from Client' && !nextTask.Published) NextStep.Responsible = 'internal'
    if (nextTask?.TaskType === 'Input from Client' && nextTask.RequireInternalConfirmation && nextTask.ClientConfirmation && !nextTask.Complete) NextStep.Responsible = 'internal'

    // Find max of all sections
    const TargetGoLive = firebase.firestore.Timestamp.fromDate(Object.values(Metadata.Sections).reduce(getMaxDueDate, false).toDate())
    Metadata.Variables.TargetGoLive = dayjs(TargetGoLive.toDate()).format('MMMM DD, YYYY')
    Metadata.Variables.StartDate = dayjs().format('MMMM DD, YYYY')
    Metadata.Variables.Client_ID = clientRef.id

    Metadata.ShowGoals = templateData.ShowGoals || false
    Metadata.Goals = templateData.Goals || {}

    console.log(Metadata)
    const newClientData = {
      BaseTemplate: {
        Name: templateData.Name,
        Ref: templateRef
      },
      Language: templateData.Language || null,
      NextStep,
      Metadata,
      TargetGoLive
    }
    if (orgData.DefaultPublic) newClientData.ClientTeam = { 'public': { 'InviteSent': true } }
    batch.update(clientRef, newClientData)
    await batch.commit()
    return Metadata
  } catch (e) {
    console.error(e)
  }

  async function compileTemplate(templateData, orgRef, preview, startDate) {
    if (!templateData.NotificationCadence) templateData.NotificationCadence = {}
    if (!templateData.NotificationCadence.ReminderDaysBeforeDue) templateData.NotificationCadence.ReminderDaysBeforeDue = 1
    if (!templateData.NotificationCadence.ReminderCadence) templateData.NotificationCadence.ReminderCadence = 5
    if (!templateData.NotificationCadence.MaxReminders) templateData.NotificationCadence.MaxReminders = 5

    const compiledArray = await Promise.all(templateData.Sections.map(async (section, s) => {
      const sectionData = (await Promise.all(section.Tasks.map(async (task, t) => {
        let Task = await taskToExternal(orgRef.collection('Tasks').doc(task.TaskID))
        if (preview) {
          const previewTask = await taskToExternal(orgRef.collection('Tasks').doc(task.TaskID).collection('Unpublished').orderBy('SavedTime', 'desc').limit(1))
          if (previewTask) Task = previewTask
        }
        if (!Task) return null
        // If Start and End Date offsets are not set for this task in this template, set it
        let prevEndDate = templateData.Sections[s].Tasks[t - 1]?.EndDateOffset || 0
        if (t === 0) prevEndDate = templateData.Sections[s - 1]?.Tasks[templateData.Sections[s - 1]?.Tasks.length - 1]?.EndDateOffset || 0
        if (!task.StartDateOffset && task.StartDateOffset != 0) task.StartDateOffset = 0 + prevEndDate
        if (!task.EndDateOffset) task.EndDateOffset = Math.min(Math.floor(Task.EstimatedDuration / 86400), 365) + task.StartDateOffset

        let NotificationCustomizations = {}
        if (task.NotificationCustomizations) {
          Object.keys(task.NotificationCustomizations).forEach(dateOffset => {
            const date = startDate.add(dateOffset, 'day').format('MM-DD-YYYY')
            NotificationCustomizations[date] = {}
            NotificationCustomizations[date].email_subject = task.NotificationCustomizations[dateOffset].email_subject ?? null
            NotificationCustomizations[date].email_top_text = task.NotificationCustomizations[dateOffset].email_top_text ?? null
            NotificationCustomizations[date].email_bottom_text = task.NotificationCustomizations[dateOffset].email_bottom_text ?? null
            NotificationCustomizations[date].email_button_text = task.NotificationCustomizations[dateOffset].email_button_text ?? null
            NotificationCustomizations[date].dont_send = task.NotificationCustomizations[dateOffset].dont_send ?? false
          })
        }

        return {
          Task,
          StartDateOffset: task.StartDateOffset,
          EndDateOffset: task.EndDateOffset,
          Dependency: task.Dependency,
          TaskID: task.TaskID,
          NotificationCustomizations
        }
      }))).filter(e => e) //use filter to remove tasks that don't exist
      return { Name: section.Name, Data: sectionData, Note: section.Note, HideOverviewPage: section.HideOverviewPage }//.reduce((map, obj, index) => (map[index] = obj, map), {}) }
    }))
    return [
      {
        //Customer: clientData.Name,
        //Sections: compiledArray.reduce((map, obj, index) => (map[index] = obj, map), {}),
        //Team: clientData.Team,
        Metrics: {
          CreationTime: firebase.firestore.FieldValue.serverTimestamp(),
          StartDate: firebase.firestore.FieldValue.serverTimestamp()
        },
        OnboardingComplete: templateData.OnboardingComplete || null,
        Homepage: templateData.Homepage || null,
        NotificationCadence: templateData.NotificationCadence
      },
      compiledArray.filter(e => e.Data.length > 0) // Remove sections with no tasks
    ]
  }
}

export async function tasksInTemplate(session, clientSession, templateid) {
  const tasks = []
  if (clientSession?.client?.Metadata?.Sections) {
    await Promise.all(Object.values(clientSession.client.Metadata.Sections).map(async section =>
      await Promise.all(Object.values(section.Data || []).map(async task => {
        const taskData = (await clientSession.clientRef.collection('Templates').doc(task.Ref).get()).data()
        tasks.push({
          id: task.TaskID,
          data: taskData
        })
      }))
    ))
  } else if (templateid) {
    const templateData = session.templateCache[templateid]
    Object.values(templateData.Sections || []).forEach(async section =>
      Object.values(section.Tasks || []).forEach(task => {
        const taskData = session.taskCache[task.TaskID]
        tasks.push({
          id: task.TaskID,
          data: taskData
        })
      })
    )
  }
  return tasks
}